// 影像牆的版面、碼流與解碼預算（plan/vms-web-v0.md §5.5 LIVE-01 / 02 / 03）。

/** LIVE-01 的版面：1 / 4 / 6 / 8 / 9 / 10 / 13 / 16 / 25 / 36。不等分的版面有大格（span）。 */
export const GRID_SIZES = [1, 4, 6, 8, 9, 10, 13, 16, 25, 36] as const;
export type GridSize = (typeof GRID_SIZES)[number];

export interface GridDef {
  cols: number;
  /** 第 i 格佔幾欄 / 幾列（沒列的是 1×1）。CSS grid 自動排版，大格放前面就會排在左上。 */
  spans: Record<number, [number, number]>;
}

export const GRIDS: Record<GridSize, GridDef> = {
  1: { cols: 1, spans: {} },
  4: { cols: 2, spans: {} },
  6: { cols: 3, spans: { 0: [2, 2] } }, // 1 大 + 5 小
  8: { cols: 4, spans: { 0: [3, 3] } }, // 1 大 + 7 小
  9: { cols: 3, spans: {} },
  10: { cols: 4, spans: { 0: [2, 2], 1: [2, 2] } }, // 2 大 + 8 小
  13: { cols: 4, spans: { 0: [2, 2] } }, // 1 大 + 12 小
  16: { cols: 4, spans: {} },
  25: { cols: 5, spans: {} },
  36: { cols: 6, spans: {} },
};

/** 自訂格線（LIVE-01，2026-09-30）：cols × rows 等分，各邊 1–8（伺服器 vms.layouts.cols 同一個範圍）。 */
export const CUSTOM_MAX = 8;

/** 一面牆：格數、欄數、大格。預設版面照 GRIDS；自訂的是 cols × (size / cols) 等分、沒有大格。 */
export interface Wall {
  size: number;
  cols: number;
  spans: Record<number, [number, number]>;
  /** 自訂格線（存伺服器時帶 cols；預設版面不帶）。 */
  custom: boolean;
}

export function isGridSize(n: number): n is GridSize {
  return (GRID_SIZES as readonly number[]).includes(n);
}

/** 格數 + 欄數 → 牆。cols = 0 / 沒給 → 預設版面（格數不是預設值就退回放得下的最小預設）；cols 合法 → 自訂。 */
export function wallOf(size: number, cols = 0): Wall {
  if (cols >= 1 && cols <= CUSTOM_MAX && size >= cols && size % cols === 0 && size / cols <= CUSTOM_MAX) {
    return { size, cols, spans: {}, custom: true };
  }
  const n: GridSize = isGridSize(size) ? size : (GRID_SIZES.find((g) => g >= size) ?? 36);
  return { size: n, cols: GRIDS[n].cols, spans: GRIDS[n].spans, custom: false };
}

/** 一格佔整面牆的比例（決定碼流）。 */
export function cellFraction(w: Wall, i: number): number {
  const [c, r] = w.spans[i] ?? [1, 1];
  const rows = Math.ceil(cellArea(w) / w.cols);
  return (c * r) / (w.cols * rows);
}

function cellArea(w: Wall): number {
  let area = 0;
  for (let i = 0; i < w.size; i++) {
    const [c, r] = w.spans[i] ?? [1, 1];
    area += c * r;
  }
  return area;
}

/** LIVE-02：格子 ≤ 1/4 畫面用子碼流，比較大（單一放大、6 / 8 格的大格）用主碼流。 */
export function streamFor(w: Wall, i: number, focused: boolean): 'main' | 'sub' {
  if (focused || w.size === 1) return 'main';
  return cellFraction(w, i) > 0.25 ? 'main' : 'sub';
}

/**
 * 拖放（LIVE-01）：把攝影機（從清單拖來）或另一格（牆上拖來）放到第 to 格。
 * 攝影機已經在牆上 → 兩格交換（不會同一台出現兩次）；格子對格子 → 交換。
 * canShow：拖來的設備不能看即時影像（例 Kinio Node、沒有 media.live）就不放，跟「放到影像牆」「指定格位」一致（Scott 工單 tkt-01M59VTMN…）。
 */
export function dropOnCell(
  cells: (string | null)[],
  to: number,
  from: { cam: string } | { cell: number },
  canShow?: (key: string) => boolean,
): (string | null)[] {
  const out = [...cells];
  if ('cam' in from && canShow && !canShow(from.cam)) return out;
  const src = 'cell' in from ? from.cell : out.indexOf(from.cam);
  if (src === to) return out;
  if (src >= 0) {
    [out[src], out[to]] = [out[to], out[src]];
  } else if ('cam' in from) {
    out[to] = from.cam;
  }
  return out;
}

/** LIVE-03：同時解碼上限，預設 16，依 CPU 核數下修（實測可再調）。 */
export function decodeBudget(cores = globalThis.navigator?.hardwareConcurrency ?? 8): number {
  if (cores >= 8) return 16;
  if (cores >= 4) return 9;
  return 4;
}

/**
 * 哪些格子即時解碼、哪些顯示預覽：大格優先，再照順序；使用者點過「轉即時」的格子（promoted）優先於其他小格。
 * 回傳即時的格子索引集合。
 */
export function liveCells(w: Wall, filled: boolean[], budget: number, promoted: Set<number> = new Set()): Set<number> {
  const order = [...Array(w.size).keys()]
    .filter((i) => filled[i])
    .sort((a, b) => {
      const big = cellFraction(w, b) - cellFraction(w, a);
      if (big !== 0) return big;
      const pa = promoted.has(a) ? 0 : 1;
      const pb = promoted.has(b) ? 0 : 1;
      return pa - pb || a - b;
    });
  return new Set(order.slice(0, budget));
}
